import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { TabbedPageLayoutComponent } from 'app/layout/page-layouts/tabbed/tabbed-page-layout.component';
import { Role } from './users.types';
import { PermissionGuard } from 'app/core/permissions/permissions.guard';

export const usersRoutes: Route[] = [
    {
        path: 'users',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        children: [
            {
                path: '',
                redirectTo: 'admins',
                pathMatch: 'full',
            },
            {
                path: 'admins',
                canActivate: [PermissionGuard],
                data: {
                    permission: 'READ_ADMIN',
                    breadcrumbs: [{ label: 'users.admins' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.new',
                            route: '/users/admins/new',
                            color: 'primary',
                            permission: 'CREATE_ADMIN',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            role: Role.ADMIN,
                            editRoute: 'admins',
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/users-list/users-list.component'
                            ).then((c) => c.UsersListComponent),
                    },
                    {
                        path: 'new',
                        canActivate: [PermissionGuard],
                        data: {
                            role: Role.ADMIN,
                            permission: 'CREATE_ADMIN',
                            breadcrumbs: [
                                {
                                    label: 'users.admins',
                                    link: '/users/admins',
                                },
                                { label: 'users.new-admin' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './components/users-form/users-form.component'
                            ).then((c) => c.UsersFormComponent),
                    },
                    {
                        path: 'automatic-groups',
                        data: {
                            role: Role.ADMIN,
                            breadcrumbs: [
                                { label: 'users.admin-automatic-groups' },
                            ],
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-auto/group-auto.component'
                            ).then((c) => c.GroupAutoComponent),
                    },
                    {
                        path: ':id',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'UPDATE_ADMIN',
                            breadcrumbs: [
                                {
                                    label: 'users.admins',
                                    link: '/users/admins',
                                },
                                { label: 'users.edit-admin' },
                            ],
                            tabs: [{ label: 'common.details', route: 'edit' }],
                        },
                        children: [
                            {
                                path: '',
                                redirectTo: 'edit',
                                pathMatch: 'full',
                            },
                            {
                                path: 'edit',
                                data: {
                                    role: Role.ADMIN,
                                },
                                loadComponent: () =>
                                    import(
                                        './components/users-form/users-form.component'
                                    ).then((c) => c.UsersFormComponent),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'admin-groups',
                data: {
                    breadcrumbs: [{ label: 'navigation.users.admin-group' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.new',
                            route: '/users/admin-groups/new',
                            color: 'primary',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            role: Role.ADMIN,
                            editRoute: 'admin-groups',
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-list/group-list.component'
                            ).then((c) => c.GroupListComponent),
                    },
                    {
                        path: 'new',
                        data: {
                            role: Role.ADMIN,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.admin-group',
                                    link: '/users/admin-groups',
                                },
                                { label: 'users.new-admin-group' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-form/group-form.component'
                            ).then((c) => c.UsersGroupFormComponent),
                    },
                    {
                        path: ':id/edit',
                        data: {
                            role: Role.ADMIN,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.admin-group',
                                    link: '/users/admin-groups',
                                },
                                { label: 'users.edit-admin-group' },
                            ],
                            tabs: [{ label: 'common.details', route: 'edit' }],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-form/group-form.component'
                            ).then((c) => c.UsersGroupFormComponent),
                    },
                ],
            },
            {
                path: 'managers',
                canActivate: [PermissionGuard],
                data: {
                    permission: 'READ_MANAGER',
                    breadcrumbs: [{ label: 'navigation.users.managers' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.new',
                            route: '/users/managers/new',
                            color: 'primary',
                            permission: 'CREATE_MANAGER',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            role: Role.MANAGER,
                            editRoute: 'managers',
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/users-list/users-list.component'
                            ).then((c) => c.UsersListComponent),
                    },
                    {
                        path: 'new',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'CREATE_MANAGER',
                            role: Role.MANAGER,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.managers',
                                    link: '/users/managers',
                                },
                                { label: 'users.new-manager' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './components/users-form/users-form.component'
                            ).then((c) => c.UsersFormComponent),
                    },
                    {
                        path: 'automatic-groups',
                        data: {
                            role: Role.MANAGER,
                            breadcrumbs: [
                                { label: 'users.manager-automatic-groups' },
                            ],
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-auto/group-auto.component'
                            ).then((c) => c.GroupAutoComponent),
                    },
                    {
                        path: ':id',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'UPDATE_MANAGER',
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.managers',
                                    link: '/users/managers',
                                },
                                { label: 'users.edit-manager' },
                            ],
                            tabs: [{ label: 'common.details', route: 'edit' }],
                        },
                        children: [
                            {
                                path: '',
                                redirectTo: 'edit',
                                pathMatch: 'full',
                            },
                            {
                                path: 'edit',
                                data: {
                                    role: Role.MANAGER,
                                },
                                loadComponent: () =>
                                    import(
                                        './components/users-form/users-form.component'
                                    ).then((c) => c.UsersFormComponent),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'manager-groups',
                data: {
                    breadcrumbs: [{ label: 'navigation.users.manager-groups' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.new',
                            route: '/users/manager-groups/new',
                            color: 'primary',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            role: Role.MANAGER,
                            editRoute: 'manager-groups',
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-list/group-list.component'
                            ).then((c) => c.GroupListComponent),
                    },
                    {
                        path: 'new',
                        data: {
                            role: Role.MANAGER,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.manager-groups',
                                    link: '/users/manager-groups',
                                },
                                { label: 'users.new-manager-group' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-form/group-form.component'
                            ).then((c) => c.UsersGroupFormComponent),
                    },
                    {
                        path: ':id/edit',
                        data: {
                            role: Role.MANAGER,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.manager-groups',
                                    link: '/users/manager-groups',
                                },
                                { label: 'users.edit-manager-group' },
                            ],
                            tabs: [{ label: 'common.details', route: 'edit' }],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-form/group-form.component'
                            ).then((c) => c.UsersGroupFormComponent),
                    },
                ],
            },
            {
                path: 'operators',
                canActivate: [PermissionGuard],
                data: {
                    permission: 'READ_OPERATOR',
                    breadcrumbs: [{ label: 'navigation.users.operators' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.new',
                            route: '/users/operators/new',
                            color: 'primary',
                            permission: 'CREATE_OPERATOR',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            role: Role.OPERATOR,
                            editRoute: 'operators',
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/users-list/users-list.component'
                            ).then((c) => c.UsersListComponent),
                    },
                    {
                        path: 'new',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'CREATE_OPERATOR',
                            role: Role.OPERATOR,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.operators',
                                    link: '/users/operators',
                                },
                                { label: 'users.new-operator' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './components/users-form/users-form.component'
                            ).then((c) => c.UsersFormComponent),
                    },
                    {
                        path: 'automatic-groups',
                        data: {
                            role: Role.OPERATOR,
                            breadcrumbs: [
                                { label: 'users.operator-automatic-groups' },
                            ],
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-auto/group-auto.component'
                            ).then((c) => c.GroupAutoComponent),
                    },
                    {
                        path: ':id',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'UPDATE_OPERATOR',
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.operators',
                                    link: '/users/operators',
                                },
                                { label: 'users.edit-operator' },
                            ],
                            tabs: [{ label: 'common.details', route: 'edit' }],
                        },
                        children: [
                            {
                                path: '',
                                redirectTo: 'edit',
                                pathMatch: 'full',
                            },
                            {
                                path: 'edit',
                                data: {
                                    role: Role.OPERATOR,
                                },
                                loadComponent: () =>
                                    import(
                                        './components/users-form/users-form.component'
                                    ).then((c) => c.UsersFormComponent),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'operator-groups',
                data: {
                    breadcrumbs: [
                        { label: 'navigation.users.operator-groups' },
                    ],
                    actions: [
                        {
                            label: 'actions.new',
                            route: '/users/operator-groups/new',
                            color: 'primary',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            role: Role.OPERATOR,
                            editRoute: 'operator-groups',
                            tabs: [],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-list/group-list.component'
                            ).then((c) => c.GroupListComponent),
                    },
                    {
                        path: 'new',
                        data: {
                            role: Role.OPERATOR,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.operator-groups',
                                    link: '/users/operator-groups',
                                },
                                { label: 'users.new-operator-group' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-form/group-form.component'
                            ).then((c) => c.UsersGroupFormComponent),
                    },
                    {
                        path: ':id/edit',
                        data: {
                            role: Role.OPERATOR,
                            breadcrumbs: [
                                {
                                    label: 'navigation.users.operator-groups',
                                    link: '/users/operator-groups',
                                },
                                { label: 'users.edit-operator-group' },
                            ],
                            tabs: [{ label: 'common.details', route: 'edit' }],
                        },
                        loadComponent: () =>
                            import(
                                './components/group-form/group-form.component'
                            ).then((c) => c.UsersGroupFormComponent),
                    },
                ],
            },
        ],
    },
];
